import React, { useState, useEffect } from 'react';
import { railBookingApi, railPriceApi, transportLegApi } from '../api/client';
import {
  RailBookingDocuments,
  RailBookingExchange,
  RailBookingForm,
  RailBookingList,
  RailBookingRefund,
} from './RailBooking';

// Querying rail prices for a reisonderdeel on a day (task 2.1d). The backend
// picks the source from the company of the leg; today only Eurostar can be
// asked live. A source that can also book gets a "Boeken" button per price,
// and the bookings made here are listed below (task 4.21).
//
// "Seats" is the number of seats left at this price, not in the class: when
// a party no longer fits, Eurostar moves the whole party to the next price
// step (measured 20/10/2026). Hence "aan deze prijs".
//
// Every search in production is stored as an observation and counts against
// the Eurostar look-to-book of at least one booking per 200 searches. That is
// why a search made shortly before is shown again, and why "Opnieuw
// opvragen" says what it costs.

const CLASS_ORDER = ['STANDARD', 'PLUS', 'PREMIER'];
const CLASS_LABEL = { STANDARD: 'Standard', PLUS: 'Plus', PREMIER: 'Premier' };
const FLEX = { 1: 'niet flexibel', 2: 'semi-flexibel', 3: 'volledig flexibel' };

const inDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

// Timetable times come as local clock time without a zone, so they are read as
// text and never converted.
const clock = (iso) => (iso ? iso.slice(11, 16) : '');
const nextDay = (dep, arr) => dep && arr && arr.slice(0, 10) > dep.slice(0, 10);

const moment = (iso) =>
  iso
    ? new Date(iso).toLocaleString('nl-BE', {
        dateStyle: 'short',
        timeStyle: 'short',
        timeZone: 'Europe/Brussels',
      })
    : '';

const money = (amount, currency) =>
  amount === null || amount === undefined
    ? ''
    : `${Number(amount).toLocaleString('nl-BE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} ${currency || ''}`.trim();

const errorText = (error, fallback) => {
  const detail = error.response && error.response.data && error.response.data.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join('; ');
  return fallback;
};

const legLabel = (l) => `${l.origin || '?'} → ${l.destination || '?'}${l.operator ? ` · ${l.operator}` : ''}`;

function FareCell({ fare, onBook }) {
  if (!fare) return <td className="number empty">-</td>;
  if (fare.status !== 'available') {
    return (
      <td className="number">
        <span className="badge no">uitverkocht</span>
      </td>
    );
  }
  return (
    <td className="number">
      <strong>{money(fare.price_per_person, fare.currency)}</strong> p.p.
      {fare.persons > 1 && (
        <div className="small-line">
          {money(fare.price, fare.currency)} voor {fare.persons}
        </div>
      )}
      <div className="small-line">
        {fare.seats !== null && fare.seats !== undefined ? `${fare.seats} aan deze prijs` : ''}
        {fare.flexibility_level ? ` · ${FLEX[fare.flexibility_level] || fare.flexibility_level}` : ''}
      </div>
      {onBook && (
        <button type="button" className="link-button" onClick={onBook}>
          Boeken
        </button>
      )}
    </td>
  );
}

function RailPrices() {
  const [legs, setLegs] = useState([]);
  const [liveCompanies, setLiveCompanies] = useState([]);
  const [legId, setLegId] = useState('');
  const [travelDate, setTravelDate] = useState(inDays(30));
  // Two by default: the history was looked up and booked for two.
  const [adults, setAdults] = useState(2);
  const [result, setResult] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  // The train and class picked for booking, and our bookings so far.
  const [picked, setPicked] = useState(null);
  const [bookings, setBookings] = useState([]);
  // The booking whose travel documents and tickets are open, and the last
  // name the screen knows for it (only right after booking).
  const [documents, setDocuments] = useState(null);
  // A refund or exchange in progress: { booking, mode, lastName }.
  const [change, setChange] = useState(null);

  const changed = (booking, text) => {
    setChange(null);
    setDocuments(null);
    setMessage({ type: 'success', text });
    loadBookings();
  };

  const open = (mode) => (booking) => {
    setDocuments(null);
    setChange({ booking, mode, lastName: '' });
  };

  const loadBookings = () =>
    railBookingApi
      .list()
      .then((r) => setBookings(Array.isArray(r.data) ? r.data : []))
      .catch(() => setBookings([]));

  useEffect(() => {
    loadBookings();
  }, []);

  const booked = (booking, lastName) => {
    setPicked(null);
    setDocuments({ booking, lastName });
    setMessage({
      type: 'success',
      text: `Geboekt: PNR ${booking.reference}, trein ${booking.service_name}, ${Number(booking.total).toLocaleString('nl-BE', {
        minimumFractionDigits: 2,
      })} ${booking.currency} voor ${booking.persons}.`,
    });
    loadBookings();
  };

  useEffect(() => {
    Promise.all([transportLegApi.list(), railPriceApi.liveCompanies()])
      .then(([legsRes, liveRes]) => {
        setLegs(Array.isArray(legsRes.data) ? legsRes.data : []);
        setLiveCompanies(Array.isArray(liveRes.data) ? liveRes.data : []);
      })
      .catch(() => setMessage({ type: 'error', text: 'De reisonderdelen konden niet opgehaald worden.' }));
  }, []);

  const isLive = (l) => liveCompanies.includes(l.operator);
  const byLabel = (a, b) => legLabel(a).localeCompare(legLabel(b), 'nl');
  const liveLegs = legs.filter(isLive).sort(byLabel);
  const otherLegs = legs.filter((l) => !isLive(l)).sort(byLabel);

  const search = async (refresh) => {
    if (!legId || !travelDate) return;
    setBusy(true);
    setMessage(null);
    setPicked(null);
    try {
      const r = await railPriceApi.search({
        transport_leg_id: Number(legId),
        travel_date: travelDate,
        adults: Number(adults),
        refresh: !!refresh,
      });
      setResult(r.data);
    } catch (error) {
      setResult(null);
      setMessage({ type: 'error', text: errorText(error, 'Het opvragen is niet gelukt.') });
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    search(false);
  };

  const classes = result
    ? CLASS_ORDER.filter((c) => result.trains.some((t) => t.fares.some((f) => f.fare_class === c))).concat(
        [...new Set(result.trains.flatMap((t) => t.fares.map((f) => f.fare_class)))].filter(
          (c) => c && !CLASS_ORDER.includes(c)
        )
      )
    : [];

  return (
    <div>
      <h2>Treinprijzen opvragen</h2>
      <p className="intro">
        Kies een reisonderdeel en een reisdatum. De bron volgt uit de maatschappij: voorlopig kan alleen Eurostar
        live opgevraagd worden, via onze eigen API, in RIT-tarieven. De prijs geldt voor het aantal reizigers
        samen: wie niet meer in een prijstrap past, gaat als groep naar de volgende. Bij Eurostar kan je een
        trein en klasse ook meteen boeken.
      </p>

      {message && <div className={`message ${message.type}`}>{message.text}</div>}

      <form onSubmit={onSubmit} className="booking-form">
        <div className="filter-bar">
          <label>
            Reisonderdeel
            <select value={legId} onChange={(e) => setLegId(e.target.value)} required>
              <option value="">kies een onderdeel</option>
              {liveLegs.length > 0 && (
                <optgroup label="Live op te vragen">
                  {liveLegs.map((l) => (
                    <option key={l.id} value={l.id}>
                      {legLabel(l)}
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label="Zonder live bron">
                {otherLegs.map((l) => (
                  <option key={l.id} value={l.id}>
                    {legLabel(l)}
                  </option>
                ))}
              </optgroup>
            </select>
          </label>
          <label>
            Reisdatum
            <input
              type="date"
              value={travelDate}
              min={inDays(0)}
              onChange={(e) => setTravelDate(e.target.value)}
              required
            />
          </label>
          <label>
            Reizigers
            <input
              type="number"
              min="1"
              max="9"
              value={adults}
              onChange={(e) => setAdults(e.target.value)}
              required
            />
          </label>
          <div className="button-row">
            <button type="submit" className="btn-primary" disabled={busy || !legId}>
              {busy ? 'Bezig' : 'Prijzen opvragen'}
            </button>
          </div>
        </div>
      </form>

      {result && !result.live && <div className="message warning">{result.message}</div>}

      {result && result.live && (
        <div>
          {result.environment === 'sandbox' && (
            <div className="message warning">
              Testomgeving: dit zijn geen echte prijzen, en er wordt niets bewaard.
            </div>
          )}

          <div className="detail-header">
            <span>
              {result.company} · {result.transport_leg_slug} · {result.travel_date} · {result.adults}{' '}
              {result.adults === 1 ? 'reiziger' : 'reizigers'}
            </span>
            <span>
              {result.reused ? 'Opgevraagd op ' : 'Nu opgevraagd, '}
              {moment(result.queried_at)}
              {result.stored ? ', bewaard als waarneming' : ''}
            </span>
            {result.searches_this_month !== null && result.searches_this_month !== undefined && (
              <span>
                Zoekopdrachten in productie deze maand: <strong>{result.searches_this_month}</strong>
              </span>
            )}
          </div>

          {result.reused && (
            <div className="message warning">
              Dit is een opvraging van {moment(result.queried_at)}, opnieuw getoond om geen zoekopdracht te
              verbruiken.{' '}
              <button type="button" className="link-button" onClick={() => search(true)} disabled={busy}>
                Toch opnieuw opvragen
              </button>{' '}
              (telt mee voor de look-to-book)
            </div>
          )}

          {result.message && <div className="message warning">{result.message}</div>}

          {result.trains.length > 0 && (
            <table className="insights-table">
              <thead>
                <tr>
                  <th>Vertrek</th>
                  <th>Aankomst</th>
                  <th>Trein</th>
                  {classes.map((c) => (
                    <th key={c} className="number">{CLASS_LABEL[c] || c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.trains.map((t) => (
                  <tr key={`${t.departure_at}-${t.service_name}`}>
                    <td>{clock(t.departure_at)}</td>
                    <td>
                      {clock(t.arrival_at)}
                      {nextDay(t.departure_at, t.arrival_at) ? ' (+1)' : ''}
                    </td>
                    <td>{t.service_name}</td>
                    {classes.map((c) => {
                      const fare = t.fares.find((f) => f.fare_class === c);
                      const canBook = result.bookable && fare && fare.status === 'available';
                      return (
                        <FareCell
                          key={c}
                          fare={fare}
                          onBook={canBook ? () => setPicked({ train: t, fare }) : null}
                        />
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {picked && (
            <RailBookingForm
              key={`${picked.train.service_name}-${picked.fare.fare_class}`}
              result={result}
              train={picked.train}
              fare={picked.fare}
              onDone={booked}
              onCancel={() => setPicked(null)}
            />
          )}
        </div>
      )}

      {documents && (
        <RailBookingDocuments
          key={documents.booking.id}
          booking={documents.booking}
          knownLastName={documents.lastName}
          onClose={() => setDocuments(null)}
        />
      )}

      <h3>Onze treinboekingen</h3>
      {change && change.mode === 'refund' && (
        <RailBookingRefund
          key={`refund-${change.booking.id}`}
          booking={change.booking}
          knownLastName={change.lastName}
          onDone={changed}
          onClose={() => setChange(null)}
        />
      )}
      {change && change.mode === 'exchange' && (
        <RailBookingExchange
          key={`exchange-${change.booking.id}`}
          booking={change.booking}
          knownLastName={change.lastName}
          onDone={changed}
          onClose={() => setChange(null)}
        />
      )}
      <RailBookingList
        bookings={bookings}
        onDocuments={(b) => {
          setChange(null);
          setDocuments({ booking: b, lastName: '' });
        }}
        onRefund={open('refund')}
        onExchange={open('exchange')}
      />
    </div>
  );
}

export default RailPrices;
